/* global responsiveVoice */
import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from 'remark-gfm';
import { Send, Volume2, VolumeX, ChevronDown } from 'lucide-react'; // Import ChevronDown for the scroll button
import { TypeAnimation } from 'react-type-animation';
import './LLM.css';
import imgHFLogo from '../../Resources/HFLogo.png';
import axiosInstance from '../../utils/axiosInstance';
import './model-dropdown.css'
import './prompt-card.css'
import PromptCard from './PromptCard';


const MarkdownMessage = ({ content }) => {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw, rehypeSanitize, rehypeHighlight]}
      components={{
        code({ node, inline, className, children, ...props }) {
          return (
            <code className={className} {...props}>
              {children}
            </code>
          );
        }
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

const AnimatedTitle = ({ title }) => {
  return (
    <div className="chat-title-container">
      <TypeAnimation
        sequence={[title]}
        wrapper="span"
        speed={50}
        cursor={false}
        className="chat-title-text"
      />
    </div>
  );
};

// const fetchChatName = async (chatId) => {
//   try {
//     const response = await axiosInstance.get(`/admin/chats/${chatId}/name`);
//     setChatTitle(response.data.chatName);
//     console.log("Chat name fetched:", response.data.chatName); // For debugging
//   } catch (error) {
//     console.error('Error fetching chat name:', error);
//     setChatTitle('New Chat'); // Fallback title if fetch fails
//   }
// };




const LLM = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null); // Track which chat's dropdown is open
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const messagesContainerRef = useRef(null); // Ref for the messages container
  const lastScrollTop = useRef(0); // Track the last scroll position
  const [chatTitle, setChatTitle] = useState('');
  const [isTypingTitle, setIsTypingTitle] = useState(false);
  const [isResponding, setIsResponding] = useState(false);
  const abortControllerRef = useRef(null);
  const [selectedModel, setSelectedModel] = useState('hf-ai-verse-v2'); // default model
  const isInitialView = messages.length === 0;


  // Start a new chat session
  const startNewChat = async () => {
    try {
      const response = await axiosInstance.post(`/admin/chats`);
      const newChatId = response.data.id;
      setCurrentChatId(newChatId);
      setChatTitle("New Chat!")
      setMessages([]);
      fetchChatHistory(); // Refresh chat history
      return newChatId; // Return the new chat ID
    } catch (error) {
      console.error('Error starting new chat:', error);
    }
  };


  const models = [
    { displayName: 'HF AI-Verse', value: 'hf-ai-verse-v2' },
    { displayName: 'Deep Think', value: 'Deep Think' }
  ];

  const promptCards = [
    {
      icon: '🔧',
      title: 'System Troubleshooting',
      description: 'Get expert diagnosis and solutions for hydraulic issues',
      prompt: "I'm experiencing pressure loss in my hydraulic system. The pressure gauge shows fluctuating readings between 80-120 bar when it should be stable at 150 bar. The system is using HLP 46 hydraulic oil, operating temperature is normal, and there's no visible leakage. Can you help me diagnose the possible causes and provide step-by-step troubleshooting instructions?"
    },
    {
      icon: '🚛',
      title: 'What is Mobile Service?',
      description: 'Get guidance on mobile service requirements',
      prompt: "what is mobile service provided by HANSA-FLEX"
    },
    {
      icon: '🔍',
      title: 'Find your hose!',
      description: 'Help with identifying and selecting the right components',
      prompt: "Give me a nice list of hoses that HANSA-FLEX provides in a tabular format."
    }
  ];

  // const [chatHistory, setChatHistory] = useState([
  //   { id: '1', name: 'Chat 1', messages: [] },
  //   { id: '2', name: 'Chat 2', messages: [] },
  // ]);

  const handleStopResponse = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsResponding(false);
      setIsTyping(false);
    }
  };

  // Log the messages array whenever it changes
  useEffect(() => {
    // console.log("message array:", messages);
  }, [messages]);

  useEffect(() => {
    // console.log('initial view: ' + isInitialView)
  }, [isInitialView])

  // Function to scroll to the bottom of the messages container
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      console.log('Scrolling to bottom');
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // Scroll to bottom whenever messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Adjust textarea height based on input
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'inherit';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [input]);

  // Track scroll position and direction
  useEffect(() => {
    const messagesContainer = messagesContainerRef.current;

    const handleScroll = () => {
      if (messagesContainer) {
        const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
        const isAtBottom = scrollHeight - (scrollTop + clientHeight) < 50;

        if (scrollTop > lastScrollTop.current) {
          setShowScrollButton(false);
        } else if (scrollTop < lastScrollTop.current) {
          setShowScrollButton(!isAtBottom);
        }

        lastScrollTop.current = scrollTop;
      }
    };

    if (messagesContainer) {
      messagesContainer.addEventListener('scroll', handleScroll);
    }

    // Cleanup
    return () => {
      if (messagesContainer) {
        messagesContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  // Add this new function to handle prompt card selection
  const handlePromptCardSelect = async (prompt) => {
    try {
      let chatId = currentChatId;
      // Start new chat if none exists
      if (!chatId) {
        chatId = await startNewChat();
        if (!chatId) {
          console.error('Failed to create new chat');
          return;
        }
      }
      // if (!currentChatId) {
      //   const response = await axiosInstance.post(`/admin/chats`);
      //   const newChatId = response.data.id;
      //   setCurrentChatId(newChatId);
      //   setChatTitle("New Chat");
      //   setMessages([]);
      //   await fetchChatHistory();
      // }

      // Send the prompt
      const userMessage = { text: prompt, sender: 'user' };
      setMessages((prev) => [...prev, userMessage]);

      await saveMessage(chatId, 'user', prompt);

      // Generate response
      const aiResponse = await sendPromptToOllama(prompt);

      if (!abortControllerRef.current) {
        await saveMessage(chatId, 'assistant', aiResponse);
      }


      let title = await generateChatTitle(prompt);
      if (title) {
        title = title.replace(/^"|"$/g, '');
        await updateChatName(chatId, title);
        setChatTitle(title);
      }
    } catch (error) {
      console.error('Error handling prompt card selection:', error);
    }
  };


  // const sendPromptToOllama = async (prompt) => {
  //   try {
  //     const response = await axiosInstance.post('/admin/send-prompt', {
  //       model: 'llama3.2:3B',
  //       prompt: prompt,
  //       stream: false,
  //     });
  //     return response.data.response;
  //   } catch (error) {
  //     console.error('Error communicating with Ollama:', error);
  //     throw error;
  //   }
  // };

  // const sendPromptToOllama = async (prompt) => {

  //   // Add an empty assistant message to the messages array

  //   try {
  //     let buffer = ''; // Buffer to accumulate chunks
  //     let result = ''; // Final result
  //     let totalReceivedLength = 0; // Track the total length of received data

  //     setIsTyping(true); // Start typing animation

  //     let _ = true;


  //     const response = await axiosInstance.post(
  //       '/admin/send-prompt',
  //       {
  //         model: 'llama3.2:3B',
  //         prompt: prompt,
  //         stream: true,
  //       },
  //       {

  //         responseType: 'text', // Use 'text' for streaming
  //         onDownloadProgress: (progressEvent) => {
  //           if (_) {
  //             setMessages((prev) => [...prev, { text: '', sender: 'assistant' }]);
  //             _ = false;
  //           }

  //           const chunk = progressEvent.event.target.responseText;
  //           setIsTyping(false); // Stop typing animation on the first chunk


  //           // Calculate the new data received
  //           const newData = chunk.slice(totalReceivedLength);
  //           totalReceivedLength = chunk.length; // Update the total received length

  //           // Append the new data to the buffer
  //           buffer += newData;

  //           // Split the buffer by newlines to handle multiple JSON objects
  //           const lines = buffer.split('\n');
  //           lines.forEach((line, index) => {
  //             if (line.trim()) {
  //               try {
  //                 const parsedChunk = JSON.parse(line);
  //                 console.log("line", parsedChunk);

  //                 const responseText = parsedChunk.response; //message
  //                 //console.log("message:", responseText);

  //                 // Update the UI with the new chunk of data
  //                 setMessages((prev) => {
  //                   const lastMessage = prev[prev.length - 1];

  //                   // If the last message is from the assistant, update it
  //                   if (lastMessage && lastMessage.sender === 'assistant') {
  //                     return [
  //                       ...prev.slice(0, -1), // Remove the last message
  //                       { ...lastMessage, text: lastMessage.text + responseText }, // Append the new chunk
  //                     ];
  //                   } else {
  //                     // If there's no last message or it's not from the assistant, add a new message
  //                     return [...prev, { text: responseText, sender: 'assistant' }];
  //                   }
  //                 });

  //                 // Append to the final result
  //                 result += responseText;
  //                 //console.log("message array:", messages);
  //               } catch (error) {
  //                 console.error('Error parsing chunk:', error);
  //               }
  //             }
  //           });

  //           // Keep the last incomplete line in the buffer
  //           buffer = lines[lines.length - 1];
  //         },
  //       }
  //     );

  //     return result;
  //   } catch (error) {
  //     console.error('Error communicating with Ollama:', error);
  //     throw error;
  //   }
  // };


  const [messageBuffer, setMessageBuffer] = useState('');

  const processMarkdownChunk = (chunk) => {
    try {
      // Accumulate chunks in buffer
      let newBuffer = messageBuffer + chunk;

      // Check for complete markdown blocks
      const isCompleteBlock = (text) => {
        // Check for unclosed code blocks, lists, etc.
        const codeBlockCount = (text.match(/```/g) || []).length;
        return codeBlockCount % 2 === 0;
      };

      if (isCompleteBlock(newBuffer)) {
        setMessages((prev) => {
          const lastMessage = prev[prev.length - 1];
          if (lastMessage && lastMessage.sender === 'assistant') {
            return [
              ...prev.slice(0, -1),
              { ...lastMessage, text: newBuffer }
            ];
          }
          return [...prev, { text: newBuffer, sender: 'assistant' }];
        });
        setMessageBuffer('');
      } else {
        setMessageBuffer(newBuffer);
      }
    } catch (error) {
      console.error('Error processing markdown chunk:', error);
    }
  };

  let responseText = '';

  const sendPromptToOllama = async (prompt) => {
    try {
      // Initialize new AbortController for this request
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsTyping(true);
      setIsResponding(true);
      let totalReceivedLength = 0;

      console.log("sent model:" + selectedModel)
      if (selectedModel === "Deep Think") {
        prompt = " first deep think  about how wil you answer and then answer about: " + prompt
      }

      const response = await axiosInstance.post(
        '/admin/send-prompt',
        {
          model: selectedModel,
          prompt: prompt,
          stream: true,
        },
        {
          responseType: 'text',
          signal: controller.signal, // Use the controller's signal
          onDownloadProgress: (progressEvent) => {

            setIsTyping(false);
            const chunk = progressEvent.event.target.responseText;
            const newData = chunk.slice(totalReceivedLength);
            totalReceivedLength = chunk.length;

            try {
              const lines = newData.split('\n');
              lines.forEach(line => {
                if (line.trim()) {
                  const parsedChunk = JSON.parse(line);
                  responseText = responseText + parsedChunk.response;
                  // Replace 'hansa-flex' with 'HANSA-FLEX' while preserving markdown formatting
                  responseText = responseText.replace(/\bhansa-flex\b/gi, 'HANSA-FLEX');
                  console.log("thisis compounded text: ---" + responseText)

                  processMarkdownChunk(responseText);
                }
              });
            } catch (error) {
              console.error('Error parsing chunk:', error);
            }
          },
        }
      );

      return responseText;
    } catch (error) {
      console.error('Error in streaming:', error);
      setIsTyping(false);
      throw error;
    } finally {
      setIsResponding(false);
      abortControllerRef.current = null;
    }
  };


  const generateChatTitle = async (firstMessage) => {
    try {
      const response = await axiosInstance.post('/admin/generate-title', {
        firstMessage: firstMessage,
      });

      const title = response.data.title;
      return title;
    } catch (error) {
      console.error('Error generating chat title:', error);
      return null;
    }
  };

  const updateChatName = async (chatId, newName) => {
    try {
      await axiosInstance.post(`/admin/chats/${chatId}/update-name`, {
        chat_name: newName,
      });

      // Update chat history state
      setChatHistory(prevHistory =>
        prevHistory.map(chat =>
          chat.id === chatId ? { ...chat, chat_name: newName } : chat
        )
      );

      // Update current chat title if this is the active chat
      if (chatId === currentChatId) {
        setChatTitle(newName);
      }
    } catch (error) {
      console.error("Error updating chat name:", error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const speakText = (text) => {
    if (typeof responsiveVoice !== 'undefined') {
      responsiveVoice.speak(text, 'UK English Female', {
        onstart: () => setIsSpeaking(true),
        onend: () => setIsSpeaking(false),
      });
    } else {
      console.error('ResponsiveVoice library is not loaded.');
    }
  };

  const stopSpeaking = () => {
    if (typeof responsiveVoice !== 'undefined') {
      responsiveVoice.cancel();
      setIsSpeaking(false);
    } else {
      console.error('ResponsiveVoice library is not loaded.');
    }
  };

  const handleRenameChat = (chatId) => {
    const newName = prompt('Enter a new name for this chat:'); // Simple prompt for renaming
    if (newName) {
      setChatHistory((prevChats) =>
        prevChats.map((chat) =>
          chat.id === chatId ? { ...chat, name: newName } : chat
        )
      );
    }
  };

  //====================================================API Endpoints=================================================================================

  // Fetch chat history on component mount
  useEffect(() => {
    fetchChatHistory();
  }, []);

  // Fetch messages for the current chat
  useEffect(() => {
    if (currentChatId) {
      fetchMessages(currentChatId);
    }
  }, [currentChatId]);

  // Add this effect to visually transition out of initial view mode when chat ID changes
  useEffect(() => {
    if (currentChatId && isInitialView) {
      // Small delay to allow smooth transition
      const timer = setTimeout(() => {
        // Force UI update to exit initial view
        setMessages(prev => [...prev]);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentChatId, isInitialView]);

  // Fetch all chat sessions
  const fetchChatHistory = async () => {
    try {
      const response = await axiosInstance.get(`/admin/chats`);
      setChatHistory(response.data);
    } catch (error) {
      console.error('Error fetching chat history:', error);
    }
  };

  // Fetch messages for a specific chat
  const fetchMessages = async (chatId) => {
    try {
      const response = await axiosInstance.get(`/admin/messages/${chatId}`);
      setMessages(response.data.messages);
      setChatTitle(response.data.chatName);

    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };



  // Save a new message
  const saveMessage = async (chatId, sender, text) => {
    try {
      await axiosInstance.post(`/admin/messages`, { chatId, sender, text });
    } catch (error) {
      console.error('Error saving message:', error);
    }
  };

  // Delete a chat session
  const deleteChat = async (chatId) => {
    try {
      await axiosInstance.delete(`/admin/chats/${chatId}`);
      if (chatId === currentChatId) {
        setMessages([]);
        setCurrentChatId(null);
      }
      fetchChatHistory(); // Refresh chat history
    } catch (error) {
      console.error('Error deleting chat:', error);
    }
  };

  // Handle sending a message
  const handleSend = async () => {
    // Validate input
    if (!input.trim()) return;

    let chatId = currentChatId;
    let userMessageText = input;

    // Clear input field early to improve UI responsiveness
    setInput('');

    // If no chat is active, create a new chat
    if (!chatId) {
      try {
        //const response = await axiosInstance.post(`/admin/chats`);
        chatId = await startNewChat();

        if (!chatId) {
          console.error('Failed to create new chat');
          return;
        }
        //const newChatId = response.data.id;
        //setCurrentChatId(newChatId); // Set the new chat as active
        //console.log('new chat id:' + newChatId)
        console.log('chatId chat id' + chatId)
        console.log('currentChatId chat id' + currentChatId)
        //await fetchChatHistory(); // Refresh chat history
        // console.log('updated active chat id' + currentChatId)
        //isInitialView = false
      } catch (error) {
        console.error('Error starting new chat:', error);
        return; // Stop further execution if chat creation fails
      }
    }

    //setMessages((prev) => [...prev, userMessage]);

    const userMessage = { text: userMessageText, sender: 'user' };
    setMessages(prev => [...prev, userMessage]);

    // Save user message to backend
    try {
      await saveMessage(chatId, 'user', userMessageText);
    } catch (error) {
      console.error('Error saving user message:', error);
      // Continue anyway to get AI response
    }

    // Generate a title if this is the first message
    if (messages.length === 0) {
      let title = await generateChatTitle(userMessageText);
      if (title) {
        try {
          title = title.replace(/^"|"$/g, '');

          // await axiosInstance.post(`/admin/chats/${currentChatId}/update-name`, {
          //   chat_name: title,
          // });

          console.log("title got is-" + title)
          await updateChatName(chatId, title);
          setChatTitle(title);
          // setTimeout(() => setIsTypingTitle(false), 1500); // Stop typing animation after .5s
          console.log("Title updated successfully:", title);
        } catch (error) {
          console.error("Error updating chat title:", error);
        }

        console.log("title is: " + title)
      }
    }

    // Use activeChat instead of currentChatId
    // await saveMessage(activeChat, 'user', input);
    // setInput('');

    //setIsTyping(true);
    try {

      const aiResponse = await sendPromptToOllama(userMessageText);

      // Save the final response to the backend
      // Only save the message if the request wasn't aborted
      if (!abortControllerRef.current) {
        await saveMessage(chatId, 'assistant', aiResponse);
      }
    } catch (error) {
      console.error('Error saving message:', error);
    } finally {
      // setIsTyping(false); // Stop typing indicator
    }

  };

  //====================================================API Endpoints=================================================================================


  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);


  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);

  const handleModelChange = (modelValue) => {
    setSelectedModel(modelValue);
    console.log("model:" + selectedModel)
    setIsModelDropdownOpen(false);
    // You might want to save the preference or trigger other actions here
  };


  return (
    <div className={`chat-container ${isInitialView ? 'initial-view' : ''}`}>
      {/* Sidebar for Chat History */}
      <div className={`llm-sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <img
              src={imgHFLogo}
              alt="HF Logo"

              className="sidebar-logo"
            />
            {!isSidebarCollapsed && (
              <span className="sidebar-title">HF AI-Verse</span>
            )}
          </div>
          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className="collapse-button"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {isSidebarCollapsed ? (
                <path d="M9 18l6-6-6-6" />
              ) : (
                <path d="M15 18l-6-6 6-6" />
              )}
            </svg>
          </button>
        </div>

        <button onClick={startNewChat} className="new-chat-button">
          <i className="fas fa-plus"></i>
          {!isSidebarCollapsed && <span>New Chat</span>}
        </button>

        {!isSidebarCollapsed && (
          <div className="chat-history">
            {chatHistory.map((chat) => (
              <div
                key={chat.id}
                className={`chat-item ${chat.id === currentChatId ? 'active' : ''}`}
                onClick={() => setCurrentChatId(chat.id)}
              >
                <span>{chat.chat_name ? chat.chat_name : "New Chat"}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteChat(chat.id);
                  }}
                  className="delete-chat-button"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0">
                    <path fillRule="evenodd" clipRule="evenodd" d="M10.5555 4C10.099 4 9.70052 4.30906 9.58693 4.75114L9.29382 5.8919H14.715L14.4219 4.75114C14.3083 4.30906 13.9098 4 13.4533 4H10.5555ZM16.7799 5.8919L16.3589 4.25342C16.0182 2.92719 14.8226 2 13.4533 2H10.5555C9.18616 2 7.99062 2.92719 7.64985 4.25342L7.22886 5.8919H4C3.44772 5.8919 3 6.33961 3 6.8919C3 7.44418 3.44772 7.8919 4 7.8919H4.10069L5.31544 19.3172C5.47763 20.8427 6.76455 22 8.29863 22H15.7014C17.2354 22 18.5224 20.8427 18.6846 19.3172L19.8993 7.8919H20C20.5523 7.8919 21 7.44418 21 6.8919C21 6.33961 20.5523 5.8919 20 5.8919H16.7799ZM17.888 7.8919H6.11196L7.30423 19.1057C7.3583 19.6142 7.78727 20 8.29863 20H15.7014C16.2127 20 16.6417 19.6142 16.6958 19.1057L17.888 7.8919ZM10 10C10.5523 10 11 10.4477 11 11V16C11 16.5523 10.5523 17 10 17C9.44772 17 9 16.5523 9 16V11C9 10.4477 9.44772 10 10 10ZM14 10C14.5523 10 15 10.4477 15 11V16C15 16.5523 14.5523 17 14 17C13.4477 17 13 16.5523 13 16V11C13 10.4477 13.4477 10 14 10Z" fill="currentColor"></path>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>

        <div className="model-selector">
          <button
            className="model-dropdown-button"
            onClick={() => setIsModelDropdownOpen(!isModelDropdownOpen)}
          >
            <span>
              {models.find(m => m.value === selectedModel)?.displayName}
            </span>
            <ChevronDown
              size={16}
              className={`chevron-icon ${isModelDropdownOpen ? 'open' : ''}`}
            />
          </button>

          {isModelDropdownOpen && (
            <div className="model-dropdown-content">
              {models.map((model) => (
                <div
                  key={model.value}
                  className={`model-option ${selectedModel === model.value ? 'selected' : ''}`}
                  onClick={() => handleModelChange(model.value)}
                >
                  {model.displayName}
                  {selectedModel === model.value && (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M20 6L9 17L4 12"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

      </div>

      {/* Main Chat Area */}
      <div className="main-chat-area">
        {isInitialView ? (
          <div className="initial-content">
            <div className="welcome-section"><br /><br /><br /><br />
              <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '0px' }}>
                <h1 className="welcome-title">
                  <img
                    src={imgHFLogo}
                    width={'50'}
                    style={{ marginBottom: '15px' }}
                    alt="HF Logo"
                  />
                  HANSA-FLEX AI Verse™
                </h1>
              </p>
              <p className="welcome-title" style={{ fontSize: '16px' }}>Your Gateway to an AI-Powered Universe.</p>
              <p className="welcome-subtitle" style={{ marginBottom: '0px' }}>How can I help you today?</p>
            </div>

            <div className="input-wrapper">
              <textarea
                ref={textareaRef}
                value={input}
                onKeyPress={handleKeyPress}
                placeholder="Message (press Enter to send, Shift+Enter for new line)"
                className="input-textarea"
                rows={1}
                maxLength={100}
                onChange={(e) => setInput(e.target.value)}
              />
              <button onClick={handleSend} disabled={!input.trim()} className="send-button">
                <Send size={20} />
              </button>
            </div>


            <div className="prompt-cards-container">
              {promptCards.map((card, index) => (
                <PromptCard
                  key={index}
                  icon={card.icon}
                  title={card.title}
                  description={card.description}
                  prompt={card.prompt}
                  onSelect={handlePromptCardSelect}
                />
              ))}
            </div>

            <div className="disclaimer">
              <span className="disclaimer-icon">⚠️</span>
              AI can make mistakes. Please verify important information.

            </div>
            <br />
            <div className="disclaimer-copyright">
              © All rights reserved HANSA-FLEX India.
            </div>
          </div>
        ) : (
          <>
            <div className='messages-container' ref={messagesContainerRef}>

              <div className="chat-title-bar">
                {!isInitialView && (
                  isTypingTitle ? (
                    <AnimatedTitle title={chatTitle} />
                  ) : (
                    <h7 className="chat-title-text">{chatTitle || 'New Chat'}</h7>
                  )
                )}
              </div>

              <div className="messages-area">
                {

                  messages.map((msg, index) => (
                    <div key={index} className={`message-wrapper ${msg.sender}`}>
                      {msg.sender === 'assistant' ? (
                        <span>
                          <img
                            src={imgHFLogo}
                            width={'30'}
                            style={{ borderRadius: '50%', border: '0.001rem solid #6b7280', marginBottom: '5px' }}
                            alt="HF Logo"
                          />
                        </span>
                      ) : (
                        <img style={{ display: 'none' }} />
                      )}

                      <div className={`message ${msg.sender}`}>
                        {msg.sender === 'assistant' ? (
                          <MarkdownMessage content={msg.text} />
                        ) : (
                          msg.text
                        )}
                      </div>


                      {/* TTS Controls for Assistant Messages */}
                      {msg.sender === 'assistant' && (
                        <div className="tts-controls">
                          <div>
                            <button
                              onClick={isSpeaking ? stopSpeaking : () => speakText(msg.text)}
                              className="tts-button"
                              title="Read aloud"
                            >
                              {isSpeaking ? <VolumeX size={16} /> : <Volume2 size={16} />}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                {isTyping && (
                  <div className="message-wrapper assistant">
                    <img
                      src={imgHFLogo}
                      width={'30'}
                      style={{ borderRadius: '50%', border: '0.0001rem solid #6b7280', marginBottom: '5px' }}
                      alt="HF Logo"
                    />
                    <div className="message assistant typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Scroll to Bottom Button */}
            {showScrollButton && (
              <button
                className="scroll-to-bottom-button"
                onClick={scrollToBottom}
                title="Scroll to bottom"
              >
                <ChevronDown size={20} />
              </button>
            )}

            <div className="input-container">
              <div className="input-wrapper">
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Message (press Enter to send, Shift+Enter for new line)"
                  className="input-textarea"
                  rows={1}
                  maxLength={100}
                  disabled={isResponding} // Disable input while responding
                />
                {isResponding ? (
                  <button
                    onClick={handleStopResponse}
                    className="stop-button"
                    title="Stop generating"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="6" y="6" width="12" height="12" fill="currentColor" />
                    </svg>
                  </button>
                ) : (
                  <button
                    onClick={handleSend}
                    disabled={!input.trim()}
                    className="send-button"
                  >
                    <Send size={20} />
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div >
  );
}

export default LLM;





{/* <button
                onClick={(e) => {
                  e.stopPropagation();
                  //handleRenameChat(chat.id); // Add your rename logic here
                  setOpenDropdownId(null); // Close dropdown
                }}
                className="dropdown-item"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 shrink-0"><path fill-rule="evenodd" clip-rule="evenodd" d="M13.2929 4.29291C15.0641 2.52167 17.9359 2.52167 19.7071 4.2929C21.4784 6.06414 21.4784 8.93588 19.7071 10.7071L18.7073 11.7069L11.6135 18.8007C10.8766 19.5376 9.92793 20.0258 8.89999 20.1971L4.16441 20.9864C3.84585 21.0395 3.52127 20.9355 3.29291 20.7071C3.06454 20.4788 2.96053 20.1542 3.01362 19.8356L3.80288 15.1C3.9742 14.0721 4.46243 13.1234 5.19932 12.3865L13.2929 4.29291ZM13 7.41422L6.61353 13.8007C6.1714 14.2428 5.87846 14.8121 5.77567 15.4288L5.21656 18.7835L8.57119 18.2244C9.18795 18.1216 9.75719 17.8286 10.1993 17.3865L16.5858 11L13 7.41422ZM18 9.5858L14.4142 6.00001L14.7071 5.70712C15.6973 4.71693 17.3027 4.71693 18.2929 5.70712C19.2831 6.69731 19.2831 8.30272 18.2929 9.29291L18 9.5858Z" fill="currentColor"></path></svg>
                Rename
              </button> */}
{/* <div className="chat-actions">
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent triggering loadChat
                    setOpenDropdownId(openDropdownId === chat.id ? null : chat.id); // Toggle dropdown
                  }}
                  className="dots-button"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="8" cy="2" r="2" />
                    <circle cx="8" cy="8" r="2" />
                    <circle cx="8" cy="14" r="2" />
                  </svg>
                </button>
                {openDropdownId === chat.id && (
                  <div className="dropdown-menu">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        //handleRenameChat(chat.id); // Add your rename logic here
                        setOpenDropdownId(null); // Close dropdown
                      }}
                      className="dropdown-item"
                    >
                      Rename
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteChat(chat.id); // Delete chat
                        setOpenDropdownId(null); // Close dropdown
                      }}
                      className="dropdown-item"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div> */}